import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { ActionsOperators } from '../../data-rules-model';
import { ICustomPositioningConfig } from '../../lib/custom-component-overlay.directive';
import { StaticOptionsProvider } from '../../lib/option-items-providers';
import { IEntityItem, PairedAutocompleteComponent } from '../../lib/paired-autocomplete/paired-autocomplete.component';
import { IActionFieldMeta } from '../../meta-data.service';
import { AllowedActions } from '../allowed-actions';

import { BaseActionComponent } from '../base-action.component';

@Component({
  selector: 'app-action-append-entries',
  templateUrl: './append-entries.component.html',
  imports: [
    PairedAutocompleteComponent
  ],
  styleUrls: ['./append-entries.component.scss']
})
export class AppendEntriesComponent extends BaseActionComponent implements AfterViewInit {

  sourceFieldControl: FormControl;
  sourceFieldDescriptorControl: FormControl;
  destinationEntityType: AbstractControl;
  sourceEntityField: AbstractControl;
  placeholder = 'Choose a field...';
  errorMessage: string;
  destinationToManyType: string;
  entitiesOptions: IEntityItem[];

  overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };

  @ViewChild('actionDestinationAppendEntries')
  private fieldPickerComponent: PairedAutocompleteComponent;

  ngAfterViewInit() {
    setTimeout(() => {
      combineLatest([this.getMeta(this.sourceFieldDescriptorControl.value),
        this.getMeta(this.destinationDescriptorControl.value)]
      ).subscribe(([srcFieldMeta, dstFieldMeta]) => {
        const srcField = srcFieldMeta.find(fm => fm.fieldName === this.sourceFieldControl.value);
        const dstField = dstFieldMeta.find(fm => fm.fieldName === this.destinationControl.value);
        this.sourceFieldControl.setValidators(Validators.required);
        this.destinationControl.setValidators(Validators.required);
        this.sourceFieldControlValueChange(srcField as IActionFieldMeta);
        this.destinationControlValueChange(dstField as IActionFieldMeta);
      });
    }, 200);
  }

  protected initialise(): void {
    this.setupControls();
    setTimeout(() => {
      combineLatest([this.getMeta(this.sourceFieldDescriptorControl && this.sourceFieldDescriptorControl.value),
        this.getMeta(this.destinationDescriptorControl.value)]
      ).subscribe(([filteredSrcMeta, filteredDstMeta]) => {
        const finalMeta = filteredSrcMeta
          .filter(fm => this.filterFields(this.destinationTypeControl.value, fm))
          .filter(fm => {
            const dstFieldMeta = filteredDstMeta.find(sfm => sfm.fieldName === this.destinationControl.value);
            return AllowedActions.actionAllowed(
              ActionsOperators[this.typeControl.value as ActionsOperators],
              dstFieldMeta,
              dstFieldMeta,
              fm
            );
          });
        this.applyNewOptionsProvider(finalMeta);
      });
    }, 0);
    this.initializeDestinationFields(this.destinationDescriptorControl.value, this.destinationControl.value);
    this.loadEntities();
    this.formGroup.valueChanges.subscribe(_ => this.checkFormStatus());
    this.subscribeToFieldChanges();
    this.setup();
  }

  setup(): void {
    if (this.actionData.source) {
      this.sourceFieldControl.setValue(this.actionData.source);
      this.sourceFieldDescriptorControl.setValue(this.actionData.sourceDescriptor);
    }
  }

  setupControls(): void {
    this.formGroup.removeControl('source');
    this.formGroup.removeControl('entityType');
    this.formGroup.removeControl('sourceEntityField');
    this.formGroup.removeControl('value');
    this.formGroup.removeControl('sourceDescriptor');
    this.destinationEntityType = new FormControl();
    this.sourceEntityField = new FormControl();
    this.sourceFieldControl = new FormControl();
    this.sourceFieldDescriptorControl = new FormControl();
    this.formGroup.addControl('entityType', this.destinationEntityType);
    this.formGroup.addControl('sourceEntityField', this.sourceEntityField);
    this.formGroup.addControl('source', this.sourceFieldControl);
    this.formGroup.addControl('sourceDescriptor', this.sourceFieldDescriptorControl);
  }

  private subscribeToFieldChanges(): void {
    this.sourceFieldControl.valueChanges.subscribe(value => {this.sourceFieldControlValueChange(value); });
    this.sourceFieldDescriptorControl.valueChanges.subscribe(value => this.sourceFieldDescriptorControlValueChangeHandler(value));
  }

  protected destinationControlValueChange(selectedField: IActionFieldMeta): void {

    if (!selectedField)
      return;

    this.destinationTypeControl.setValue(selectedField.metaType);

    this.getMeta(this.sourceFieldDescriptorControl.value)
      .subscribe((filteredMeta: IActionFieldMeta[]) => {
        const finalMeta = filteredMeta
          .filter(fm => this.filterFields(selectedField.metaType, fm))
          .filter(fm => AllowedActions.actionAllowed(
            ActionsOperators[this.typeControl.value as ActionsOperators],
            selectedField,
            selectedField,
            fm
          ));

        this.applyNewOptionsProvider(finalMeta);

      });

  }

  sourceFieldControlValueChange(selectedField: IActionFieldMeta): void {

    if (!selectedField)
      return;

    this.sourceTypeControl.setValue(selectedField.metaType);

    this.getMeta(this.sourceFieldDescriptorControl.value).subscribe(meta => {
      const sourceMeta = meta.find(srcMeta => srcMeta.fieldName === this.sourceFieldControl.value);
      this.sourceTypeControl.setValue(sourceMeta.metaType);
      this.sourceEntityField.setValue(sourceMeta.entityType + '.' + sourceMeta.toManyType);
    });
  }

  sourceFieldDescriptorControlValueChangeHandler(desc: string | null) {

    setTimeout(() => {

      combineLatest([
          this.getMeta(desc),
          this.getMeta(this.destinationDescriptorControl.value)
        ]
      )
        .subscribe(([filteredSrcMeta, filteredDstMeta]) => {
          const finalMeta = filteredSrcMeta
            .filter(fm => this.filterFields(this.destinationTypeControl.value, fm))
            .filter(fm => {
              const dstField = filteredDstMeta.find(sfm => sfm.fieldName === this.destinationControl.value);
              return AllowedActions.actionAllowed(
                ActionsOperators[this.typeControl.value as ActionsOperators],
                dstField,
                dstField,
                fm
              );
            });
          const meta = finalMeta.find(fm => fm.fieldName === this.sourceFieldControl.value);
          this.applyNewOptionsProvider(finalMeta);
          this.sourceFieldControlValueChange(meta);
        });
    }, 0);
  }

  private applyNewOptionsProvider(meta: IActionFieldMeta[]) {
    setTimeout(() => {
      const options = meta
        .filter(fm => this.filterFields(this.destinationTypeControl.value, fm))
        .map(fm => ({name: fm.fieldName, label: fm.label}));
      if (options.length === 0) {
        this.errorMessage = 'There are no supported fields for the selected action.';
        this.fieldPickerComponent.disableOptionsAutocomplete(true);
        this.sourceFieldControl.setValue(null, {emitEvent: false});
        this.checkFormStatus();

      } else {
        this.errorMessage = '';
      }
      this.placeholder = this.errorMessage;
      const optionsProvider = new StaticOptionsProvider(options);
      this.fieldPickerComponent.setOptionsProvider(optionsProvider, this.sourceFieldControl.value);
    }, 0);
  }

  initializeDestinationFields(descriptor: string, value: string) {
    this.getMeta(descriptor).subscribe(meta => {
      const fieldMeta = meta.find(srcMeta => srcMeta.fieldName === value);
      this.destinationEntityType.setValue(fieldMeta.toManyType);
      this.destinationToManyType = fieldMeta.toManyType;
    });

  }

  private filterFields(destinationType: string, sourceMeta: IActionFieldMeta): boolean {
    const sourceType = sourceMeta ? sourceMeta.metaType : '';
    // TODO Check why the sourceDescriptorControl.value is set as an empty string when the component is initialized
    const sourceDescriptionValue = this.sourceFieldDescriptorControl.value === '' ? null : this.sourceFieldDescriptorControl.value;
    return (destinationType === sourceType)
      && (this.destinationToManyType === sourceMeta.toManyType)
      && (sourceDescriptionValue !== this.destinationDescriptorControl.value)
      && (!sourceMeta || !sourceMeta.readOnly);
  }

  protected  loadEntities(): void {

    this.metaDataService.reduceMetaToEntitiesOptions(this.primaryEntity, this.dataSourceType)
      .subscribe(entities => {
        let entityMeta: IActionFieldMeta[];
        entities.forEach(entity => {
          entity.secondaryDescriptor ?
            this.getMeta(entity.secondaryDescriptor).subscribe(meta => entityMeta = meta) :
            this.getMeta(null).subscribe(meta => entityMeta = meta);
          const entityField = entityMeta.find(entityMetaField => entityMetaField.toManyType === this.destinationToManyType);
          if (entityField)
            this.entitiesOptions.push(entity);
        });
      });
  }

  checkFormStatus() {
    if (this.sourceFieldControl.value === null || this.sourceFieldControl.value === undefined
      || this.sourceFieldControl.value.trim().length <= 0) {
      this.formGroup.setErrors({invalid: true});
    } else if (this.sourceFieldControl.value !== null
      && this.sourceFieldControl.value.trim().length >= 0 ) {
      console.log(`Form valid!!`);
      this.formGroup.setErrors(null);
    }
  }

}
